import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const SecuritySettingsCard = () => {
  const [allowedDomains, setAllowedDomains] = useState([
    'example.com',
    'app.example.com'
  ]);
  const [newDomain, setNewDomain] = useState('');
  const [requireSSL, setRequireSSL] = useState(true);
  const [enableCORS, setEnableCORS] = useState(true);

  const handleAddDomain = () => {
    if (newDomain && !allowedDomains?.includes(newDomain)) {
      setAllowedDomains([...allowedDomains, newDomain]);
      setNewDomain('');
    }
  };

  const handleRemoveDomain = (domain) => {
    setAllowedDomains(allowedDomains?.filter(d => d !== domain));
  };

  return (
    <div className="bg-card border border-border rounded-md p-4 md:p-6 space-y-6">
      <div>
        <h3 className="text-lg font-heading font-semibold text-foreground">
          Security Settings
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Configure security policies for form embedding
        </p>
      </div>
      <div className="space-y-4">
        <Checkbox
          label="Require SSL/HTTPS"
          description="Only allow forms to be embedded on secure pages"
          checked={requireSSL}
          onChange={(e) => setRequireSSL(e?.target?.checked)}
        />
        
        <Checkbox
          label="Enable CORS"
          description="Allow cross-origin requests from specified domains"
          checked={enableCORS}
          onChange={(e) => setEnableCORS(e?.target?.checked)}
        />
      </div>
      <div className="pt-4 border-t border-border">
        <label className="text-sm font-medium text-foreground mb-3 block">
          Allowed Domains
        </label>
        <p className="text-xs text-muted-foreground mb-3">
          Restrict form embedding to specific domains. Leave empty to allow all domains.
        </p>

        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1">
              <Input
                type="text"
                value={newDomain}
                onChange={(e) => setNewDomain(e?.target?.value)}
                placeholder="yourdomain.com"
                onKeyPress={(e) => e?.key === 'Enter' && handleAddDomain()}
              />
            </div>
            <Button
              variant="outline"
              size="default"
              iconName="Plus"
              iconPosition="left"
              onClick={handleAddDomain}
              className="sm:flex-shrink-0"
            >
              Add Domain
            </Button>
          </div>

          {allowedDomains?.length > 0 && (
            <div className="space-y-2">
              {allowedDomains?.map((domain, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-muted rounded-md"
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Icon name="Globe" size={16} color="var(--color-muted-foreground)" />
                    <span className="text-sm text-foreground truncate">
                      {domain}
                    </span>
                  </div>
                  <button
                    onClick={() => handleRemoveDomain(domain)}
                    className="p-1.5 rounded hover:bg-destructive/10 transition-smooth flex-shrink-0"
                    aria-label={`Remove ${domain}`}
                  >
                    <Icon name="X" size={16} color="var(--color-destructive)" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="pt-4 border-t border-border">
        <div className="flex items-start gap-3 p-3 bg-warning/10 rounded-md">
          <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-warning">
              Security Notice
            </p>
            <p className="text-xs text-warning/80 mt-1">
              Restricting domains will prevent your form from loading on unauthorized websites. Test thoroughly before deploying.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecuritySettingsCard;